import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { dashboardInteractiveFocusSx } from "../Dashboard/dashboard.styles";
import {
  dashboard2DayColumnSx,
  dashboard2WeekGridSx,
  dashboard2WeekHeroSectionSx,
  DASHBOARD2_TOP_ROW_ACTIONS_WIDTH_PX,
  DASHBOARD2_TOP_ROW_BREAKPOINT_PX,
  DASHBOARD2_TOP_ROW_COLLEAGUES_MAX_WIDTH_PX,
  DASHBOARD2_TOP_ROW_FAVORITE_WIDTH_PX,
  DASHBOARD2_TOP_ROW_TODAY_FLEX_BASIS_PX,
  DASHBOARD2_TODAY_SIDE_MIN_WIDTH_PX
} from "./dashboard2.styles";
import {
  dashboard2AdditionalBookingBadgeTextSx,
  dashboard2SectionLabelBaseSx
} from "./dashboard2-typography.styles";

export {
  dashboard2AdditionalBookingBadgeTextSx,
  dashboard2DayHeaderTextSx,
  dashboard2MetadataTextSx,
  dashboard2PageIntroTitleSx,
  dashboard2SectionDescriptionSx,
  dashboard2SectionFieldsetLegendDescriptionSx,
  dashboard2SectionFieldsetLegendSx,
  dashboard2SectionFieldsetLegendTitleSx,
  dashboard2SectionIntroTitleSx,
  dashboard2SectionLabelBaseSx,
  dashboard2TertiaryLinkTextSx,
  dashboard2WeekNavLabelButtonSx,
  dashboard2WeekPickerCenterLabelSx,
  dashboard2WeekPlanTitleSx
} from "./dashboard2-typography.styles";
import {
  holidayStatusSurfaceColors,
  statusAccentColor,
  statusSurfaceInkColors,
  weekDayHolidayCardSurface,
  weekDayStatusInkColors
} from "./utils/dashboard2-status-display.utils";
import type { DayWorkStatusKind } from "./types/dashboard2-work-status.types";
import {
  DASHBOARD2_WEEK_DAY_CARD_HEIGHT_PX,
  DASHBOARD2_WEEK_DAY_ICON_RAIL_WIDTH_PX,
  DASHBOARD2_WEEK_DAY_ROW_PADDING_Y,
  DASHBOARD2_WEEK_DAY_STATUS_ROW_HEIGHT_PX,
  type WeekDayCardActionKind
} from "./utils/dashboard2-week-day-card.utils";

const dashboard2SectionBorderRadius = 3;

/** 44×44 minimum hit area below lg and on coarse pointers (WCAG 2.5.5). */
export const dashboard2CompactTouchTargetSx = (theme: Theme): SxProps<Theme> => ({
  boxSizing: "border-box",
  [theme.breakpoints.down("lg")]: {
    minHeight: 44,
    minWidth: 44
  },
  "@media (pointer: coarse)": {
    minHeight: 44,
    minWidth: 44
  }
});

/** Top row: equal-height grid cells. */
export const dashboard2TopRowGridCellSx: SxProps<Theme> = {
  display: "flex",
  minWidth: { xs: 0, md: 300 },
  height: "100%",
  alignSelf: "stretch"
};

/**
 * Top row: Today — dominant card; grows to fill remaining row width and stretches to match row height.
 */
export const dashboard2TopRowTodayCellSx = (isWideRow: boolean): SxProps<Theme> => ({
  display: "flex",
  width: "100%",
  minWidth: 0,
  height: isWideRow ? "100%" : "auto",
  alignSelf: isWideRow ? "stretch" : "flex-start",
  flex: isWideRow ? `1 1 ${DASHBOARD2_TOP_ROW_TODAY_FLEX_BASIS_PX}px` : undefined,
  minInlineSize: isWideRow ? DASHBOARD2_TOP_ROW_TODAY_FLEX_BASIS_PX : 0
});

export const dashboard2TopRowTodayCellContentSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  minWidth: 0,
  height: "100%",
  alignSelf: "flex-start",
  "& > *": {
    width: "100%",
    boxSizing: "border-box"
  }
};

/**
 * Top row: Colleagues — secondary context beside Today; fixed width, stretches to match row height.
 */
export const dashboard2TopRowFlexibleCellSx = (isWideRow: boolean): SxProps<Theme> => ({
  display: "flex",
  width: isWideRow ? DASHBOARD2_TOP_ROW_COLLEAGUES_MAX_WIDTH_PX : "100%",
  minWidth: 0,
  height: isWideRow ? "100%" : "auto",
  alignSelf: isWideRow ? "stretch" : "flex-start",
  flex: isWideRow ? `0 0 ${DASHBOARD2_TOP_ROW_COLLEAGUES_MAX_WIDTH_PX}px` : undefined,
  minInlineSize: isWideRow ? DASHBOARD2_TOP_ROW_COLLEAGUES_MAX_WIDTH_PX : 0,
  maxWidth: isWideRow ? DASHBOARD2_TOP_ROW_COLLEAGUES_MAX_WIDTH_PX : "none"
});

export const dashboard2TopRowColleaguesCellContentSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  maxWidth: DASHBOARD2_TOP_ROW_COLLEAGUES_MAX_WIDTH_PX,
  minWidth: 0,
  height: "100%",
  "& > *": {
    width: "100%",
    boxSizing: "border-box"
  }
};

/** Top row: favorite location cell (carousel reuse; not mounted on dashboard top row). */
export const dashboard2TopRowFavoriteCellSx = (theme: Theme) => ({
  display: "flex",
  height: "100%",
  alignSelf: "stretch",
  [theme.breakpoints.up(DASHBOARD2_TOP_ROW_BREAKPOINT_PX)]: {
    flex: `0 0 ${DASHBOARD2_TOP_ROW_FAVORITE_WIDTH_PX}px`,
    width: DASHBOARD2_TOP_ROW_FAVORITE_WIDTH_PX,
    maxWidth: DASHBOARD2_TOP_ROW_FAVORITE_WIDTH_PX
  }
});

/** Top row: Quick actions — compact utility, never the row-height driver. */
export const dashboard2TopRowQuickActionsCellSx = (isWideRow: boolean): SxProps<Theme> => ({
  display: "flex",
  width: isWideRow ? DASHBOARD2_TOP_ROW_ACTIONS_WIDTH_PX : "100%",
  minWidth: 0,
  alignSelf: "flex-start",
  flex: isWideRow ? `0 0 ${DASHBOARD2_TOP_ROW_ACTIONS_WIDTH_PX}px` : undefined,
  maxWidth: isWideRow ? DASHBOARD2_TOP_ROW_ACTIONS_WIDTH_PX : "none"
});

export const dashboard2TopRowQuickActionsCellContentSx = (isWideRow: boolean): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  minWidth: 0,
  alignSelf: "flex-start",
  maxWidth: isWideRow ? DASHBOARD2_TOP_ROW_ACTIONS_WIDTH_PX : "none",
  "& > *": {
    width: "100%",
    boxSizing: "border-box",
    height: "auto"
  }
});

export const dashboard2TopRowCellContentSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  width: "100%",
  minWidth: 0,
  height: "100%",
  "& > *": {
    flex: 1,
    minHeight: 0,
    width: "100%",
    boxSizing: "border-box"
  }
};

export const dashboard2TodayStatusHeadlineSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1,
  width: "100%",
  minWidth: 0
};

export const dashboard2TodayStatusHeadlineBodySx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  rowGap: 1,
  flex: 1,
  minWidth: 0,
  flexWrap: "wrap"
};

export const dashboard2TodayStatusTextColumnSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0
};

/** Today headline — hug booking text; grow on narrow widths so detail lines can wrap. */
export const dashboard2TodayStatusHeadlineTextSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: "1 1 12rem",
  minWidth: 0,
  maxWidth: "100%"
};

export const dashboard2TodayStatusHeadlineClickableSx = (theme: Theme): SxProps<Theme> =>
  [
    dashboardInteractiveFocusSx,
    dashboard2TodayStatusHeadlineTextSx,
    {
      alignItems: "flex-start",
      justifyContent: "flex-start",
      boxSizing: "border-box",
      p: 0,
      border: 0,
      borderRadius: 1,
      bgcolor: "transparent",
      textAlign: "left",
      color: "inherit",
      "&:hover": {
        bgcolor: "action.hover"
      },
      [theme.breakpoints.down("lg")]: {
        minHeight: 44,
        minWidth: 44
      },
      "@media (pointer: coarse)": {
        minHeight: 44,
        minWidth: 44
      }
    }
  ] as SxProps<Theme>;

/** Long inventory names wrap; time stays on its own line below. */
export const dashboard2StatusDetailWrapSx: SxProps<Theme> = {
  minWidth: 0,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word"
};

/** Status pill headline — vertically center icon + text (incl. single-line pills). */
export const dashboard2StatusPillHeadlineSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  width: "100%",
  minWidth: 0,
  flex: 1
};

/** Fixed pill height — single- and two-line week cells stay aligned. */
export const DASHBOARD2_STATUS_PILL_HEIGHT = 54;

const dashboard2StatusPillLayoutSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  width: "100%",
  minHeight: DASHBOARD2_STATUS_PILL_HEIGHT,
  height: DASHBOARD2_STATUS_PILL_HEIGHT,
  boxSizing: "border-box",
  overflow: "hidden",
  px: 1,
  py: 0.75,
  borderRadius: 1.5,
  textAlign: "left"
};

export const dashboard2TodayStatusHeadlineActionSx: SxProps<Theme> = {
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  gap: 1
};

/** Matches schedule detail show-place actions — keeps icon + text buttons aligned. */
export const DASHBOARD2_TODAY_HEADLINE_ACTION_BUTTON_HEIGHT_PX = 36.5;

/** Inline today headline actions (floor plan, check-in) — same height at every breakpoint. */
export const dashboard2TodayHeadlineActionButtonSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboard2TodayCheckInButtonSx(theme),
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
  height: DASHBOARD2_TODAY_HEADLINE_ACTION_BUTTON_HEIGHT_PX,
  minHeight: DASHBOARD2_TODAY_HEADLINE_ACTION_BUTTON_HEIGHT_PX,
  maxHeight: DASHBOARD2_TODAY_HEADLINE_ACTION_BUTTON_HEIGHT_PX,
  minWidth: "auto",
  py: 0,
  [theme.breakpoints.down("lg")]: {
    height: DASHBOARD2_TODAY_HEADLINE_ACTION_BUTTON_HEIGHT_PX,
    minHeight: DASHBOARD2_TODAY_HEADLINE_ACTION_BUTTON_HEIGHT_PX,
    maxHeight: DASHBOARD2_TODAY_HEADLINE_ACTION_BUTTON_HEIGHT_PX,
    minWidth: "auto",
    py: 0
  },
  "@media (pointer: coarse)": {
    height: DASHBOARD2_TODAY_HEADLINE_ACTION_BUTTON_HEIGHT_PX,
    minHeight: DASHBOARD2_TODAY_HEADLINE_ACTION_BUTTON_HEIGHT_PX,
    maxHeight: DASHBOARD2_TODAY_HEADLINE_ACTION_BUTTON_HEIGHT_PX,
    minWidth: "auto"
  }
});

export const dashboard2TodayHeadlineActionsContainerSx = (theme: Theme): SxProps<Theme> => ({
  "& .MuiButton-root": {
    width: "auto",
    ...(dashboard2TodayHeadlineActionButtonSx(theme) as object)
  }
});

export const dashboard2TodayWorkStatusCollapseSx: SxProps<Theme> = {
  width: "100%",
  borderTop: 1,
  borderColor: "divider",
  pt: 0.75,
  mt: 0.25
};

export const dashboard2TodayWorkStatusTriggerSx = (theme: Theme): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  gap: 0.5,
  px: 0.5,
  py: { xs: 0.75, md: 0.5 },
  border: 0,
  borderRadius: 1,
  bgcolor: "transparent",
  color: "inherit",
  cursor: "pointer",
  textAlign: "left",
  boxSizing: "border-box",
  ...dashboardInteractiveFocusSx,
  ...(dashboard2CompactTouchTargetSx(theme) as object),
  "&:hover": {
    bgcolor: theme.palette.action.hover
  },
  "&:disabled": {
    cursor: "not-allowed",
    opacity: theme.palette.action.disabledOpacity,
    "&:hover": {
      bgcolor: "transparent"
    }
  }
});

/** Unplanned today trigger — light info tint so “Tap to plan today” reads as a CTA. */
export const dashboard2TodayWorkStatusPlanTriggerSx = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  const accentColor = theme.palette.info.main;

  return {
    ...dashboard2TodayWorkStatusTriggerSx(theme),
    px: 1,
    py: { xs: 0.875, md: 0.75 },
    border: `1px solid ${alpha(accentColor, isDark ? 0.45 : 0.35)}`,
    borderRadius: 1.5,
    color: accentColor,
    bgcolor: alpha(accentColor, isDark ? 0.14 : 0.08),
    "&:hover": {
      bgcolor: alpha(accentColor, isDark ? 0.22 : 0.12),
      borderColor: accentColor
    },
    "&:disabled": {
      cursor: "not-allowed",
      opacity: theme.palette.action.disabledOpacity,
      "&:hover": {
        bgcolor: alpha(accentColor, isDark ? 0.14 : 0.08),
        borderColor: alpha(accentColor, isDark ? 0.45 : 0.35)
      }
    }
  };
};

export const dashboard2TodayWorkStatusTriggerLabelSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  minWidth: 0,
  flex: 1
};

/** User status pill + optional extra-bookings line — stacked, natural height. */
export const dashboard2PlanningDayUserSectionSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: 0.25,
  flexShrink: 0,
  width: "100%",
  minWidth: 0
};

export const dashboard2PlanningDayColleaguesSectionSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  flex: 1,
  minHeight: 0,
  width: "100%"
};

export const dashboard2PlanningDayContentSx = (isMobile: boolean): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  ...(isMobile
    ? {
        maxHeight: 140
      }
    : {})
});

export const dashboard2TodayStatusIconSx = (theme: Theme, accentColor: string): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: 32,
  height: 32,
  borderRadius: 1,
  color: accentColor,
  bgcolor: alpha(accentColor, theme.palette.mode === "dark" ? 0.2 : 0.1)
});

/** Tier-1 hero legend — accent follows today's work status. */
export const dashboard2TodayStatusLegendSx = (
  theme: Theme,
  kind: DayWorkStatusKind
): SxProps<Theme> => {
  if (kind === "unplanned") {
    return {
      ...dashboard2SectionLabelBaseSx,
      color: "text.secondary"
    };
  }

  if (kind === "public_holiday") {
    return {
      ...dashboard2SectionLabelBaseSx,
      color: holidayStatusSurfaceColors(theme).color
    };
  }

  return {
    ...dashboard2SectionLabelBaseSx,
    color: statusAccentColor(theme, kind)
  };
};

/** Tier-2/4 section legends — neutral, subordinate to the today hero. */
export const dashboard2NeutralSectionLegendSx: SxProps<Theme> = {
  ...dashboard2SectionLabelBaseSx,
  color: "text.secondary"
};

export const dashboard2SectionFieldsetLegendIconSx = (theme: Theme): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: 1,
  color: "text.secondary",
  bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.1 : 0.06),
  "& svg": {
    fontSize: 18
  }
});

export const dashboard2PrimaryFieldsetLegendIconSx = (theme: Theme): SxProps<Theme> => {
  const accent =
    theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.main;

  return {
    ...dashboard2SectionFieldsetLegendIconSx(theme),
    color: accent,
    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.2 : 0.1)
  };
};

export const dashboard2StatusFieldsetLegendIconSx = (
  theme: Theme,
  kind: DayWorkStatusKind
): SxProps<Theme> => {
  if (kind === "unplanned") {
    return dashboard2SectionFieldsetLegendIconSx(theme);
  }

  if (kind === "public_holiday") {
    const { color } = holidayStatusSurfaceColors(theme);

    return {
      ...dashboard2SectionFieldsetLegendIconSx(theme),
      color,
      bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.2 : 0.12)
    };
  }

  const accent = statusAccentColor(theme, kind);

  return {
    ...dashboard2SectionFieldsetLegendIconSx(theme),
    color: accent,
    bgcolor: alpha(accent, theme.palette.mode === "dark" ? 0.2 : 0.1)
  };
};

export const dashboard2StatusFieldsetLegendTitleSx = (
  theme: Theme,
  kind: DayWorkStatusKind
): SxProps<Theme> => {
  if (kind === "unplanned") {
    return { color: "text.primary" };
  }

  if (kind === "public_holiday") {
    return { color: holidayStatusSurfaceColors(theme).color };
  }

  return { color: statusAccentColor(theme, kind) };
};

/**
 * Tier-1 today hero — single emphasized surface per viewport.
 * Semantic uniform border + wash; legend and icon carry accent (no side-tab stripe).
 */
export const dashboard2TodayStatusFieldsetSx = (
  theme: Theme,
  kind: DayWorkStatusKind
): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  const accent = statusAccentColor(theme, kind);

  if (kind === "unplanned") {
    const inks = statusSurfaceInkColors(theme, kind);

    return {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: dashboard2SectionBorderRadius,
      margin: 0,
      p: 1.5,
      width: "100%",
      height: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      bgcolor: isDark ? alpha(theme.palette.common.white, 0.04) : theme.palette.grey[50],
      color: inks.primary
    };
  }

  if (kind === "public_holiday") {
    const { bgcolor, border } = holidayStatusSurfaceColors(theme);

    return {
      border: `1px solid ${border}`,
      borderRadius: dashboard2SectionBorderRadius,
      margin: 0,
      p: 1.5,
      width: "100%",
      height: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      bgcolor
    };
  }

  const inks = statusSurfaceInkColors(theme, kind);

  return {
    border: `1px solid ${alpha(accent, 0.35)}`,
    borderRadius: dashboard2SectionBorderRadius,
    margin: 0,
    p: 1.5,
    width: "100%",
    height: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    bgcolor: alpha(accent, isDark ? 0.12 : 0.06),
    color: inks.primary
  };
};

/** Tier-2 neutral panel — favorites, quick actions (no primary wash). */
export const dashboard2NeutralSectionFieldsetSx = (theme: Theme): SxProps<Theme> => ({
  border: 1,
  borderColor: "divider",
  borderRadius: dashboard2SectionBorderRadius,
  margin: 0,
  p: 1.5,
  width: "100%",
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  bgcolor: theme.palette.background.paper,
  boxShadow: "none"
});

/** Section stack — header above bordered panel body. */
export const dashboard2SectionPanelSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  width: "100%",
  height: "100%",
  minWidth: 0,
  minHeight: 0
};

export const dashboard2AdditionalBookingBadgesRowSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: 0.5,
  width: "100%",
  pl: 5,
  mt: 1
};

const dashboard2TodayWideContentBaseSx: SxProps<Theme> = {
  alignItems: "flex-start",
  gap: 2,
  width: "100%",
  minWidth: 0
};

/** Hint-only wide interior: main shrinks; side sizes to the soft-hint copy. */
export const dashboard2TodayWideHintContentSx: SxProps<Theme> = {
  ...dashboard2TodayWideContentBaseSx,
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto"
};

/** Bookings wide interior: proportional 2:1 split with a readable side list. */
export const dashboard2TodayWideBookingsContentSx: SxProps<Theme> = {
  ...dashboard2TodayWideContentBaseSx,
  display: "grid",
  gridTemplateColumns: `minmax(0, 2fr) minmax(${DASHBOARD2_TODAY_SIDE_MIN_WIDTH_PX}px, 1fr)`
};

export const dashboard2TodayWideContentSx = (showInlineBookings: boolean): SxProps<Theme> =>
  showInlineBookings ? dashboard2TodayWideBookingsContentSx : dashboard2TodayWideHintContentSx;

export const dashboard2TodayMainColumnSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  minWidth: 0
};

export const dashboard2TodaySideColumnSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: 0.75,
  width: "100%",
  minWidth: 0,
  pl: 2,
  borderLeft: 1,
  borderColor: "divider"
};

export const dashboard2TodaySideEmptySx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  alignSelf: "center",
  gap: 0.5,
  width: "100%",
  minWidth: 0,
  pl: 2,
  py: 2,
  borderLeft: 1,
  borderColor: "divider"
};

export const dashboard2TodaySideHintSx = (theme: Theme): SxProps<Theme> => ({
  fontSize: "0.875rem",
  lineHeight: 1.4,
  fontWeight: 600,
  maxWidth: 180,
  color: theme.palette.text.secondary
});

export const dashboard2TodaySideHintPointerSx = (theme: Theme): SxProps<Theme> => ({
  fontSize: "0.75rem",
  lineHeight: 1.4,
  fontWeight: 400,
  maxWidth: 180,
  color: theme.palette.text.disabled
});

export const dashboard2TodaySideBookingsSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
  width: "100%",
  minWidth: 0
};

export const dashboard2TodaySideBookingTextSx: SxProps<Theme> = {
  ...(dashboard2StatusDetailWrapSx as object),
  width: "100%"
};

export const dashboard2TodaySideBookingRowSx = (interactive: boolean): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 0.125,
  width: "100%",
  minWidth: 0,
  px: 0.75,
  py: 0.5,
  borderRadius: 1,
  border: 1,
  borderColor: "divider",
  bgcolor: "background.paper",
  textAlign: "left",
  color: "text.primary",
  ...(interactive
    ? {
        cursor: "pointer",
        "&:hover": {
          bgcolor: "action.hover"
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "info.main",
          outlineOffset: 2
        }
      }
    : {})
});

/** Muted presentation when today's primary booking has ended. */
export const dashboard2FinishedStatusContentSx = (theme: Theme): SxProps<Theme> => ({
  opacity: theme.palette.mode === "dark" ? 0.72 : 0.66
});

export const dashboard2AdditionalBookingBadgeSx = (
  theme: Theme,
  kind?: DayWorkStatusKind
): SxProps<Theme> => {
  const ink = kind ? statusSurfaceInkColors(theme, kind).secondary : theme.palette.text.secondary;

  return {
    display: "inline-flex",
    alignItems: "center",
    px: 0.75,
    py: 0.25,
    borderRadius: 1,
    ...dashboard2AdditionalBookingBadgeTextSx,
    color: ink,
    bgcolor: theme.palette.action.hover,
    border: 1,
    borderColor: "divider"
  };
};

/** Shared type scale for “+N more bookings” overflow links. */
const dashboard2ExtraBookingsLinkTypographySx = {
  fontSize: "0.75rem",
  lineHeight: 1.25,
  fontWeight: 600,
  py: { xs: 0.75, md: 0.375 }
} as const;

/** “+N more bookings” — hue-aligned link on tinted status surfaces. */
export const dashboard2TodayAdditionalBookingsLinkSx = (
  theme: Theme,
  kind: DayWorkStatusKind
): SxProps<Theme> => {
  const ink = statusSurfaceInkColors(theme, kind);

  return {
    ...dashboardInteractiveFocusSx,
    ...dashboard2ExtraBookingsLinkTypographySx,
    ...(dashboard2CompactTouchTargetSx(theme) as object),
    alignSelf: "flex-start",
    px: 0.75,
    textTransform: "none",
    color: ink.primary,
    textDecoration: "underline",
    textUnderlineOffset: "0.2em",
    "&:hover": {
      color: ink.secondary,
      bgcolor: alpha(ink.primary, theme.palette.mode === "dark" ? 0.12 : 0.08)
    }
  };
};

export const dashboard2TodayStatusActionsSx: SxProps<Theme> = {
  width: "100%",
  borderTop: 1,
  borderColor: "divider",
  pt: 1,
  mt: 0.25
};

/** Today hero check-in — 44px touch target on tablet / touch. */
export const dashboard2TodayCheckInButtonSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboardInteractiveFocusSx,
  ...(dashboard2CompactTouchTargetSx(theme) as object),
  flexShrink: 0,
  textTransform: "none",
  px: { xs: 1.5, md: 1.25 },
  py: 0.25,
  whiteSpace: "nowrap",
  [theme.breakpoints.down("lg")]: {
    py: 0.75
  }
});

/** Dashboard2 page — natural height; vertical scroll on app-parent. */
export const dashboard2PageSx: SxProps<Theme> = {
  width: "100%",
  flex: "0 0 auto",
  height: "auto",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  overflow: "visible"
};

/** Calendar / table — same natural-height page stack as week layout. */
export const dashboard2PageCalendarTableSx: SxProps<Theme> = dashboard2PageSx;

export const dashboard2ScheduleSectionNaturalSx: SxProps<Theme> = {
  flexShrink: 0,
  width: "100%",
  overflow: "visible",
  pb: 2
};

/** Schedule chrome + week board — tighter than page stack spacing. */
export const dashboard2ScheduleWeekClusterSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  flexShrink: 0,
  width: "100%"
};

export const dashboard2PageIntroSx: SxProps<Theme> = {
  flexShrink: 0,
  width: "100%",
  minWidth: 0,
  overflow: "hidden",
  pt: 2,
  pb: 2
};

export const dashboard2ManageColleaguesLinkSx = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  const linkColor = isDark ? theme.palette.primary.light : theme.palette.primary.main;

  return {
    ...dashboardInteractiveFocusSx,
    // Enlarge hit area only for touch — not by viewport width (avoids icon/label shift on resize).
    boxSizing: "border-box",
    "@media (pointer: coarse)": {
      minHeight: 44,
      minWidth: 44
    },
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    py: 0.5,
    fontSize: "0.875rem",
    fontWeight: 600,
    lineHeight: 1,
    fontKerning: "normal",
    color: linkColor,
    "& .MuiSvgIcon-root": {
      fontSize: 18,
      display: "block",
      flexShrink: 0
    },
    "&:hover": {
      color: isDark ? theme.palette.common.white : theme.palette.primary.light
    }
  };
};

/** Manage-colleagues shortcut in the schedule chrome — soft primary pill with icon. */
export const dashboard2ManageColleaguesPillSx = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  const primary = theme.palette.primary.main;

  return {
    ...dashboardInteractiveFocusSx,
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    px: 1,
    py: 0.5,
    minHeight: 34,
    borderRadius: 999,
    border: `1px solid ${alpha(primary, isDark ? 0.45 : 0.3)}`,
    color: isDark ? theme.palette.primary.light : theme.palette.primary.main,
    fontSize: "0.8125rem",
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    textDecoration: "none",
    transition: theme.transitions.create(["background-color", "border-color", "color"], {
      duration: theme.transitions.duration.shorter
    }),
    "& .MuiSvgIcon-root": {
      fontSize: "1.05rem"
    },
    "&:hover": {
      textDecoration: "none",
      bgcolor: alpha(primary, isDark ? 0.26 : 0.14),
      borderColor: alpha(primary, isDark ? 0.6 : 0.45),
      color: isDark ? "primary.light" : "primary.dark"
    }
  };
};

export const dashboard2SectionIntroSx: SxProps<Theme> = {
  flexShrink: 0,
  width: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 0.5
};

/** Section copy inside a tinted status fieldset — secondary ink from the wash hue. */
export const dashboard2StatusSurfaceSecondaryTextSx = (
  theme: Theme,
  kind: DayWorkStatusKind
): SxProps<Theme> => ({
  color: statusSurfaceInkColors(theme, kind).secondary
});

export const dashboard2WeekPlanIntroSx: SxProps<Theme> = {
  flexShrink: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  mb: 0.5
};

export const dashboard2WeekPlanProgressHeaderSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 1.5,
  width: "100%",
  minWidth: 0
};

export const dashboard2WeekPlanProgressMetaSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 1.5,
  width: "100%",
  minWidth: 0
};

export const dashboard2WeekPlanProgressBarSx: SxProps<Theme> = {
  width: "100%",
  height: 4,
  borderRadius: 999,
  bgcolor: theme => alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.12 : 0.08),
  "& .MuiLinearProgress-bar": {
    borderRadius: 999
  }
};

export const dashboard2WeekDayCardSx = (
  theme: Theme,
  options: {
    isToday?: boolean;
    isUnplanned?: boolean;
    isHoliday?: boolean;
  }
): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  const { isToday = false, isUnplanned = false, isHoliday = false } = options;
  const isCompact = isUnplanned || isHoliday;
  const defaultColumnBg = isDark ? alpha(theme.palette.common.white, 0.04) : theme.palette.grey[50];
  const holidayCardSurface = weekDayHolidayCardSurface(theme);

  return {
    border: isHoliday
      ? `1px solid ${holidayCardSurface.border}`
      : `1px ${isUnplanned ? "dashed" : "solid"} ${
          isToday ? alpha(theme.palette.primary.main, 0.35) : theme.palette.divider
        }`,
    borderRadius: 1,
    p: 1.25,
    width: "100%",
    ...(isCompact
      ? {
          height: "auto",
          minHeight: 0,
          maxHeight: "none",
          alignSelf: "flex-start"
        }
      : {
          height: DASHBOARD2_WEEK_DAY_CARD_HEIGHT_PX,
          minHeight: DASHBOARD2_WEEK_DAY_CARD_HEIGHT_PX,
          maxHeight: DASHBOARD2_WEEK_DAY_CARD_HEIGHT_PX
        }),
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    bgcolor: isHoliday
      ? holidayCardSurface.bgcolor
      : isToday
        ? alpha(theme.palette.primary.main, isDark ? 0.14 : 0.06)
        : defaultColumnBg,
    overflow: "hidden"
  };
};

/** Week-planning pills may grow when inventory names wrap — keep min height aligned with default pills. */
export const dashboard2WeekPlanningStatusPillLayoutSx: SxProps<Theme> = {
  minHeight: DASHBOARD2_STATUS_PILL_HEIGHT,
  height: "auto",
  overflow: "visible",
  alignItems: "flex-start",
  py: 0.625
};

export const dashboard2WeekDayRowDividerColor = (theme: Theme): string =>
  alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.28 : 0.15);

export const dashboard2WeekDayCardHeaderSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 0.5,
  flexShrink: 0,
  minWidth: 0,
  pb: 0.25
};

/** Planned-day check — quiet secondary tone, not success green. */
export const dashboard2WeekDayPlannedCheckSx = (theme: Theme): SxProps<Theme> => ({
  fontSize: 16,
  color: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.55 : 0.42)
});

export const dashboard2WeekDayCardDayLabelSx = (theme: Theme, isToday = false): SxProps<Theme> => ({
  fontSize: "1rem",
  fontWeight: 600,
  lineHeight: 1.3,
  textAlign: "left",
  display: "inline-block",
  width: "fit-content",
  pb: 0.375,
  borderBottom: 1,
  borderColor: dashboard2WeekDayRowDividerColor(theme),
  color: isToday
    ? theme.palette.mode === "dark"
      ? theme.palette.primary.light
      : theme.palette.primary.main
    : "text.primary"
});

export const dashboard2WeekDayPrimaryTextSx: SxProps<Theme> = {
  fontSize: "0.875rem",
  lineHeight: 1.35,
  fontWeight: 600
};

export const dashboard2WeekDaySecondaryTextSx: SxProps<Theme> = {
  fontSize: "0.8125rem",
  lineHeight: 1.35
};

export const dashboard2WeekDayMetaTextSx: SxProps<Theme> = {
  fontSize: "0.8125rem",
  lineHeight: 1.4
};

export const dashboard2WeekDayCardBodySx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  gap: 0
};

const dashboard2WeekDayRowPaddingSx: SxProps<Theme> = {
  py: DASHBOARD2_WEEK_DAY_ROW_PADDING_Y,
  boxSizing: "border-box"
};

export const dashboard2WeekDayIconRailRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1,
  width: "100%",
  minWidth: 0,
  flexShrink: 0
};

/** Row 1 — fixed height for two-line office/holiday and single-line mobile/not-set. */
export const dashboard2WeekDayStatusRowSx: SxProps<Theme> = {
  ...dashboard2WeekDayIconRailRowSx,
  ...dashboard2WeekDayRowPaddingSx,
  alignItems: "center",
  height: DASHBOARD2_WEEK_DAY_STATUS_ROW_HEIGHT_PX,
  minHeight: DASHBOARD2_WEEK_DAY_STATUS_ROW_HEIGHT_PX,
  maxHeight: DASHBOARD2_WEEK_DAY_STATUS_ROW_HEIGHT_PX
};

/** Interactive row 1 — opens the day status sheet (non-holiday days only). */
export const dashboard2WeekDayStatusRowButtonSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboard2WeekDayStatusRowSx,
  width: "100%",
  textAlign: "left",
  color: "inherit",
  borderRadius: 2,
  transition: theme.transitions.create("background-color", {
    duration: theme.transitions.duration.short
  }),
  ...dashboardInteractiveFocusSx,
  "&:hover": {
    bgcolor: theme.palette.action.hover
  }
});

/** Row 2 — booking pills aligned with the status icon rail. */
export const dashboard2WeekDayBookingsRowSx: SxProps<Theme> = {
  ...dashboard2WeekDayIconRailRowSx,
  ...dashboard2WeekDayRowPaddingSx
};

export const dashboard2WeekDayIconRailSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: DASHBOARD2_WEEK_DAY_ICON_RAIL_WIDTH_PX,
  minWidth: DASHBOARD2_WEEK_DAY_ICON_RAIL_WIDTH_PX,
  height: DASHBOARD2_WEEK_DAY_ICON_RAIL_WIDTH_PX,
  flexShrink: 0,
  color: "inherit",
  py: 1.5
};

export const dashboard2WeekDayStatusLabelSx = (
  theme: Theme,
  kind: DayWorkStatusKind
): SxProps<Theme> => ({
  minWidth: 0,
  flex: 1,
  color:
    kind === "unplanned"
      ? theme.palette.text.secondary
      : weekDayStatusInkColors(theme, kind).primary,
  lineHeight: 1.3
});

export const dashboard2WeekDayStatusSecondaryTextSx = (
  theme: Theme,
  kind: DayWorkStatusKind
): SxProps<Theme> => ({
  color: weekDayStatusInkColors(theme, kind).secondary
});

export const dashboard2WeekDayStatusIconSx = (
  theme: Theme,
  accentColor: string
): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: 1,
  color: accentColor,
  bgcolor: alpha(accentColor, theme.palette.mode === "dark" ? 0.2 : 0.1)
});

export const dashboard2WeekDayStatusTextColumnSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.25,
  minWidth: 0,
  flex: 1
};

export const dashboard2WeekDayRowDividerSx = (theme: Theme): SxProps<Theme> => ({
  flexShrink: 0,
  borderColor: dashboard2WeekDayRowDividerColor(theme),
  my: 0
});

export const dashboard2WeekDayActionAreaSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flexShrink: 0,
  width: "100%",
  ...dashboard2WeekDayRowPaddingSx
};

export const dashboard2WeekDayBookingPillsRowSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-start",
  gap: 0.5,
  minWidth: 0,
  flex: 1
};

export const dashboard2WeekDayBookingPillSx = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";

  return {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: 0.5,
    px: 1,
    py: 0.375,
    borderRadius: 999,
    border: `1px solid ${theme.palette.divider}`,
    bgcolor: isDark ? alpha(theme.palette.common.white, 0.06) : theme.palette.background.paper,
    color: theme.palette.text.primary,
    maxWidth: "100%",
    minWidth: 0,
    fontSize: "0.8125rem",
    lineHeight: 1.35,
    ...dashboardInteractiveFocusSx,
    "& .MuiSvgIcon-root": {
      color: "currentColor"
    },
    "& svg path": {
      fill: "currentColor"
    },
    "&:hover": {
      bgcolor: isDark ? alpha(theme.palette.common.white, 0.1) : theme.palette.action.hover
    }
  };
};

export const dashboard2WeekDayBookingOverflowPillSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboard2WeekDayBookingPillSx(theme),
  ...(dashboard2CompactTouchTargetSx(theme) as object),
  cursor: "pointer",
  [theme.breakpoints.down("lg")]: {
    px: 1.25,
    py: 0.625
  }
});

export const dashboard2WeekDayColleagueRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  minWidth: 0,
  flexShrink: 0,
  ...dashboard2WeekDayRowPaddingSx
};

export const dashboard2WeekDayActionButtonSx = (
  theme: Theme,
  action: WeekDayCardActionKind
): SxProps<Theme> => {
  const isPlanDay = action === "plan_day";
  const isTintedAccentPlan = isPlanDay;
  const isDark = theme.palette.mode === "dark";
  const accentColor = isPlanDay ? theme.palette.info.main : theme.palette.text.primary;
  const borderColor = isTintedAccentPlan ? accentColor : theme.palette.divider;
  const floatingShadow =
    theme.palette.mode === "dark"
      ? `0 2px 6px ${alpha(theme.palette.common.black, 0.38)}, 0 1px 0 ${alpha(theme.palette.common.white, 0.06)}`
      : `0 1px 2px ${alpha(theme.palette.common.black, 0.05)}, 0 4px 10px ${alpha(theme.palette.common.black, 0.1)}`;
  const floatingShadowHover =
    theme.palette.mode === "dark"
      ? `0 4px 14px ${alpha(theme.palette.common.black, 0.48)}, 0 1px 0 ${alpha(theme.palette.common.white, 0.08)}`
      : `0 2px 4px ${alpha(theme.palette.common.black, 0.07)}, 0 8px 18px ${alpha(theme.palette.common.black, 0.12)}`;

  return {
    alignSelf: "center",
    flexShrink: 0,
    width: "auto",
    maxWidth: "100%",
    height: "auto",
    px: 1.25,
    py: 0.625,
    minHeight: 32,
    ...(dashboard2CompactTouchTargetSx(theme) as object),
    borderRadius: 1.5,
    border: `1px solid ${borderColor}`,
    color: accentColor,
    bgcolor: isTintedAccentPlan
      ? alpha(accentColor, isDark ? 0.12 : 0.06)
      : theme.palette.background.paper,
    boxShadow: isTintedAccentPlan
      ? `${floatingShadow}, 0 2px 8px ${alpha(accentColor, isDark ? 0.22 : 0.14)}`
      : floatingShadow,
    fontSize: "0.875rem",
    fontWeight: 600,
    textTransform: "none",
    transition: theme.transitions.create(
      ["background-color", "box-shadow", "border-color", "transform"],
      {
        duration: theme.transitions.duration.short
      }
    ),
    ...dashboardInteractiveFocusSx,
    "&:hover": {
      bgcolor: alpha(accentColor, isDark ? 0.2 : 0.1),
      borderColor: isTintedAccentPlan ? accentColor : alpha(accentColor, isDark ? 0.4 : 0.28),
      boxShadow: `${floatingShadowHover}, 0 4px 12px ${alpha(accentColor, isDark ? 0.28 : 0.18)}`,
      transform: "translateY(-1px)"
    },
    "&:active": {
      transform: "translateY(0)",
      boxShadow: `${floatingShadow}, 0 1px 4px ${alpha(accentColor, isDark ? 0.16 : 0.1)}`
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover, &:active": {
        transform: "none"
      }
    }
  };
};

export const dashboard2WeekDayActionContentSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
  minWidth: 0,
  maxWidth: "100%"
};

export const dashboard2WeekDayActionIconSx: SxProps<Theme> = {
  display: "inline-flex",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center"
};

export const dashboard2WeekDayActionLabelSx: SxProps<Theme> = {
  minWidth: 0,
  textAlign: "center",
  lineHeight: 1.25,
  whiteSpace: "normal",
  overflowWrap: "anywhere"
};

export const dashboard2WeekSectionNaturalSx: SxProps<Theme> = {
  flexShrink: 0,
  width: "100%",
  overflow: "visible"
};

/** Grows to absorb space below today row + schedule chrome (desktop week only). */
export const dashboard2WeekSectionGrowSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  width: "100%",
  overflow: "hidden"
};

/** Calendar / table embed — fills remaining viewport; inner surface scrolls when needed. */
export const dashboard2ScheduleSectionGrowSx: SxProps<Theme> = {
  flex: "1 1 0",
  minHeight: 0,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  overflow: "hidden"
};

/** Week board shell — content grows; page scrolls via app-parent. */
export const dashboard2WeekPlanningShellSx: SxProps<Theme> = {
  flex: "0 0 auto",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  overflow: "visible"
};

export const dashboard2WeekHeroFillSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboard2WeekHeroSectionSx(theme),
  flex: 1,
  minHeight: 0,
  maxHeight: "100%",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  p: { xs: 2, md: 2 }
});

export const dashboard2WeekGridFillSx = (isMobile: boolean, rowCount = 5): SxProps<Theme> => ({
  ...dashboard2WeekGridSx(isMobile),
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
  gridTemplateRows: isMobile ? `repeat(${rowCount}, minmax(0, 1fr))` : "minmax(0, 1fr)",
  alignContent: "stretch"
});

export const dashboard2WeekPlanningHeroFillSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboard2WeekHeroSectionSx(theme),
  flex: "0 0 auto",
  width: "100%",
  maxWidth: "100%",
  overflow: "visible",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  p: { xs: 2, md: 2 }
});

export const dashboard2WeekPlanningMobileCarouselSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  alignItems: "flex-start",
  gap: 1,
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  minHeight: DASHBOARD2_WEEK_DAY_CARD_HEIGHT_PX,
  overflowX: "auto",
  overflowY: "hidden",
  overscrollBehaviorX: "contain",
  WebkitOverflowScrolling: "touch",
  scrollSnapType: "x mandatory",
  scrollPaddingInline: 8,
  px: 0.25,
  scrollbarWidth: "thin"
};

export const dashboard2WeekPlanningMobileDaySx: SxProps<Theme> = {
  flex: "0 0 auto",
  width: "min(92%, 22rem)",
  minWidth: 280,
  maxWidth: 360,
  scrollSnapAlign: "start",
  scrollSnapStop: "always"
};

export const dashboard2WeekPlanningGridFillSx = (isCompactViewport: boolean): SxProps<Theme> =>
  isCompactViewport
    ? {
        ...dashboard2WeekPlanningMobileCarouselSx,
        flex: "0 0 auto"
      }
    : {
        ...dashboard2WeekGridSx(false),
        flex: "0 0 auto",
        gridTemplateColumns: "repeat(5, minmax(200px, 1fr))",
        gridTemplateRows: "auto",
        alignContent: "start",
        alignItems: "start"
      };

export const dashboard2DayColumnStretchSx: SxProps<Theme> = {
  height: "100%",
  minHeight: 0
};

export const dashboard2DayColumnWithStretchSx = (
  theme: Theme,
  isToday = false,
  interactive = false
): SxProps<Theme> =>
  ({
    ...(dashboard2DayColumnSx(theme, isToday, interactive) as object),
    ...(dashboard2DayColumnStretchSx as object)
  }) as SxProps<Theme>;

export const dashboard2WeekNavFillSx: SxProps<Theme> = {
  flexShrink: 0
};

/** Tier-3 schedule chrome — neutral bar, no primary wash. */
export const dashboard2ScheduleChromeSx = (theme: Theme): SxProps<Theme> => ({
  p: 2,
  mb: 0,
  borderRadius: dashboard2SectionBorderRadius,
  border: 1,
  borderColor: "divider",
  bgcolor: theme.palette.background.paper,
  boxShadow: "none"
});

/** Minimal tier-3 chrome — layout toggle row + collapsed filter. */
export const dashboard2ScheduleChromeMinimalSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboard2ScheduleChromeSx(theme),
  p: 1,
  display: "flex",
  flexDirection: "column",
  gap: 1
});

export const dashboard2ScheduleChromeRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  minWidth: 0
};

export const dashboard2ScheduleFilterPanelSx: SxProps<Theme> = {
  pt: 0.5,
  borderTop: 1,
  borderColor: "divider"
};

export const dashboard2WeekColleagueStatusRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  minWidth: 0,
  width: "100%"
};

/** Non-interactive overflow caption — no link styling when details are unavailable. */
export const dashboard2WeekExtraBookingsCaptionSx = (
  theme: Theme,
  kind: DayWorkStatusKind
): SxProps<Theme> => ({
  ...dashboard2ExtraBookingsLinkTypographySx,
  display: "block",
  flexShrink: 0,
  pl: 0.25,
  textAlign: "left",
  color: statusSurfaceInkColors(theme, kind).secondary
});

/** Opens day bookings dialog when additional booking details exist. */
export const dashboard2WeekExtraBookingsButtonSx = (
  theme: Theme,
  kind: DayWorkStatusKind
): SxProps<Theme> => {
  const ink = statusSurfaceInkColors(theme, kind);

  return {
    ...dashboard2ExtraBookingsLinkTypographySx,
    ...(dashboard2CompactTouchTargetSx(theme) as object),
    display: "block",
    flexShrink: 0,
    pl: 0.25,
    textAlign: "left",
    alignSelf: "flex-start",
    px: 0.25,
    textTransform: "none",
    color: ink.secondary,
    cursor: "pointer",
    "&:focus-visible": {
      outline: "2px solid",
      outlineColor: "info.main",
      outlineOffset: 2
    },
    "&:hover": {
      bgcolor: "action.hover",
      color: ink.primary
    }
  };
};

export const DASHBOARD2_SCHEDULE_FILTER_PANEL_ID = "dashboard2-schedule-filter-panel";

export const dashboard2IconTouchTargetSx: SxProps<Theme> = {
  width: 44,
  height: 44,
  minWidth: 44,
  minHeight: 44
};

export const dashboard2FavoriteBookButtonSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboardInteractiveFocusSx,
  ...(dashboard2CompactTouchTargetSx(theme) as object),
  minHeight: 32,
  py: 0.25,
  px: 1.25,
  lineHeight: 1.25,
  [theme.breakpoints.down("lg")]: {
    py: 0.625
  }
});

export const dashboard2FilterTriggerTouchTargetSx: SxProps<Theme> = {
  ...dashboard2IconTouchTargetSx
};

/** Favorite location tile — tier-2 neutral (today hero carries primary emphasis). */
export const dashboard2FavoriteCarouselSectionSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboard2NeutralSectionFieldsetSx(theme),
  overflow: "visible"
});

/** Fluid TopList tile — fills the narrow favorite column (not fixed 260px). */
export const dashboard2FavoriteCarouselTileSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  width: "95%",
  minWidth: 0,
  maxWidth: "95%",
  alignSelf: "center",
  boxSizing: "border-box"
};

export const dashboard2FavoriteCarouselStageSx: SxProps<Theme> = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  width: "100%"
};

const dashboard2FavoriteCarouselEdgeArrowSx = (
  theme: Theme,
  edge: "previous" | "next"
): SxProps<Theme> => {
  const arrowBg = theme.palette.background.paper;
  const arrowBorder = `1px solid ${theme.palette.divider}`;
  const arrowShadow = theme.shadows[1];
  const arrowHoverShadow = theme.shadows[4];
  const arrowColor = theme.palette.text.primary;
  const translate = edge === "previous" ? "translate(-50%, -50%)" : "translate(50%, -50%)";
  const popTransform = `${translate} scale(1.12)`;
  const pressTransform = `${translate} scale(1.04)`;

  const staticArrowState = {
    bgcolor: arrowBg,
    backgroundColor: arrowBg,
    border: arrowBorder,
    boxShadow: arrowShadow,
    color: arrowColor
  };

  return {
    position: "absolute",
    top: "50%",
    zIndex: 1,
    width: 44,
    height: 44,
    minWidth: 44,
    minHeight: 44,
    p: 0,
    ...(edge === "previous" ? { left: 0 } : { right: 0 }),
    transform: translate,
    transition: theme.transitions.create(["transform", "box-shadow", "border-color"], {
      duration: theme.transitions.duration.short
    }),
    ...staticArrowState,
    "&&:hover:not(.Mui-disabled)": {
      ...staticArrowState,
      transform: popTransform,
      boxShadow: arrowHoverShadow,
      borderColor: alpha(theme.palette.primary.main, 0.35),
      "@media (prefers-reduced-motion: reduce)": {
        transform: translate
      }
    },
    "&&:active:not(.Mui-disabled)": {
      ...staticArrowState,
      transform: pressTransform,
      boxShadow: theme.shadows[2],
      "@media (prefers-reduced-motion: reduce)": {
        transform: translate
      }
    },
    "&&.Mui-focusVisible": {
      ...staticArrowState,
      outline: "2px solid",
      outlineColor: "info.main",
      outlineOffset: 2
    },
    "&&.Mui-disabled": {
      ...staticArrowState,
      color: theme.palette.action.disabled,
      opacity: 0.45,
      transform: translate
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none"
    }
  };
};

export const dashboard2FavoriteCarouselPreviousArrowSx = (theme: Theme): SxProps<Theme> =>
  dashboard2FavoriteCarouselEdgeArrowSx(theme, "previous");

export const dashboard2FavoriteCarouselNextArrowSx = (theme: Theme): SxProps<Theme> =>
  dashboard2FavoriteCarouselEdgeArrowSx(theme, "next");

export const dashboard2DayColumnContentSx = (isMobile: boolean): SxProps<Theme> =>
  isMobile
    ? {
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        maxHeight: 140,
        overflowY: "auto",
        flex: 1,
        minHeight: 0
      }
    : {
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        flex: 1
      };

export const dashboard2StatusPillSx = (
  theme: Theme,
  accentColor: string,
  kind: DayWorkStatusKind,
  interactive = false
): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  const inks = statusSurfaceInkColors(theme, kind);
  const pillBg = interactive
    ? isDark
      ? alpha(accentColor, 0.14)
      : alpha(accentColor, 0.16)
    : isDark
      ? alpha(theme.palette.common.white, 0.06)
      : alpha(accentColor, 0.1);
  const borderAlpha = interactive ? (isDark ? 0.52 : 0.46) : isDark ? 0.45 : 0.35;

  return {
    ...dashboard2StatusPillLayoutSx,
    gap: 0.25,
    color: inks.primary,
    border: `1px solid ${alpha(accentColor, borderAlpha)}`,
    bgcolor: pillBg,
    ...(interactive
      ? {
          ...dashboardInteractiveFocusSx,
          cursor: "pointer",
          transition: theme.transitions.create(["background-color", "box-shadow"], {
            duration: theme.transitions.duration.short
          }),
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none"
          },
          "&:hover": {
            bgcolor: isDark ? alpha(accentColor, 0.2) : alpha(accentColor, 0.22),
            boxShadow: theme.shadows[1]
          }
        }
      : {})
  };
};

/** Public holiday pill — matches main schedule holiday bar (#F1F5F9). */
export const dashboard2HolidayStatusPillSx = (
  bgcolor: string,
  border: string,
  color: string
): SxProps<Theme> => ({
  ...dashboard2StatusPillLayoutSx,
  gap: 0.25,
  color,
  border: `1px solid ${border}`,
  bgcolor
});

/** Colleague booking chips — softer wash, no heavy left stripe; supports the user's row. */
export const dashboard2ColleagueBookingChipSx = (
  theme: Theme,
  accentColor: string
): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  const chipBg = isDark ? alpha(theme.palette.common.white, 0.04) : alpha(accentColor, 0.06);
  const chipBgHover = isDark ? alpha(theme.palette.common.white, 0.07) : alpha(accentColor, 0.1);

  return {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 0,
    px: 1,
    py: 0.75,
    borderRadius: 1.5,
    width: "100%",
    boxSizing: "border-box",
    textAlign: "left",
    color: "text.primary",
    border: `1px solid ${alpha(accentColor, isDark ? 0.24 : 0.2)}`,
    bgcolor: chipBg,
    transition: theme.transitions.create(["box-shadow", "background-color"], {
      duration: theme.transitions.duration.short
    }),
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none"
    },
    "&:hover": {
      bgcolor: chipBgHover,
      boxShadow: theme.shadows[1]
    },
    "&:focus-visible": {
      outline: "2px solid",
      outlineColor: "info.main",
      outlineOffset: 2
    }
  };
};

export const dashboard2AdditionalBookingRowSx = (interactive: boolean): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 0.25,
  width: "100%",
  p: 1,
  mb: 0.75,
  borderRadius: 1,
  border: 1,
  borderColor: "divider",
  bgcolor: "background.paper",
  textAlign: "left",
  ...(interactive
    ? {
        cursor: "pointer",
        "&:hover": {
          bgcolor: "action.hover"
        }
      }
    : {})
});

export const dashboard2TopRowStatusQuickActionsSx = (isWideRow: boolean): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
  width: "100%",
  flex: "0 1 auto",
  minHeight: 0,
  maxHeight: isWideRow ? 190 : "none",
  overflowY: isWideRow ? "auto" : "visible",
  pr: isWideRow ? 0.25 : 0
});

export const dashboard2ScheduleLoadErrorAlertSx: SxProps<Theme> = {
  borderRadius: 3,
  alignItems: "center"
};

export const dashboard2QuickActionsEmptySx: SxProps<Theme> = {
  width: "100%"
};

export const dashboard2WeekPickerNavSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 0.5,
  mb: 0.5
};

export const dashboard2WeekPickerListSx: SxProps<Theme> = {
  maxHeight: 320,
  overflowY: "auto"
};

/** Week picker rows — keep selected/hover states readable on dark dialog surfaces. */
export const dashboard2WeekPickerOptionSx = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  const selectedBg = alpha(theme.palette.primary.main, isDark ? 0.28 : 0.12);
  const selectedHoverBg = alpha(theme.palette.primary.main, isDark ? 0.34 : 0.16);
  const selectedPrimaryColor = isDark ? theme.palette.text.primary : theme.palette.primary.main;

  return {
    borderRadius: 1,
    color: "text.primary",
    ...dashboardInteractiveFocusSx,
    "& .MuiListItemText-primary": {
      color: "text.primary",
      fontWeight: 500
    },
    "& .MuiListItemText-secondary": {
      color: "text.secondary"
    },
    "&.Mui-selected.Mui-selected": {
      bgcolor: selectedBg,
      color: selectedPrimaryColor,
      "& .MuiListItemText-primary": {
        color: selectedPrimaryColor,
        fontWeight: 600
      },
      "& .MuiListItemText-secondary": {
        color: isDark ? theme.palette.text.secondary : alpha(theme.palette.primary.main, 0.78)
      },
      "&:hover": {
        bgcolor: selectedHoverBg
      }
    },
    "&:hover": {
      bgcolor: "action.hover"
    }
  };
};

export { dashboardToggleButtonGroupSx as dashboard2ToggleButtonGroupSx } from "../Dashboard/dashboard-toggle-button-group.styles";
